import { useEffect, useMemo, useState } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
  BarChart, Bar, Cell, PieChart, Pie,
} from "recharts";
import {
  LogOut, ArrowLeft, TrendingUp, TrendingDown, Wallet, Activity, Award, Calendar,
  Plug, AlertCircle, Loader2, RefreshCw, CheckCircle2, ArrowDownToLine,
  ArrowDownCircle, ArrowUpCircle, Download, Percent,
  HelpCircle, Lock, ChevronDown, X as XIcon, PlayCircle,
  ShieldCheck, Briefcase, History, FileText, LineChart as LineChartIcon,
} from "lucide-react";
import { useAuth, authClient } from "../lib/auth";
import { LOGO_URL } from "../lib/brand";
import { BACKEND_URL } from "../lib/apiBase";
import { QACommunityBanner } from "../components/QACommunity";

const API = `${BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { value: "7d",  short: "7T",  label: "Letzte 7 Tage" },
  { value: "30d", short: "30T", label: "Letzte 30 Tage" },
  { value: "90d", short: "90T", label: "Letzte 90 Tage" },
  { value: "1y",  short: "1J",  label: "Letztes Jahr" },
  { value: "all", short: "Max", label: "Gesamte Zeit" },
];
const RANGE_LABELS = Object.fromEntries(RANGE_OPTIONS.map((o) => [o.value, o.label]));

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [range, setRange] = useState("90d");
  const [tab, setTab] = useState("performance");
  // MT5 Investor-Password help box — visible by default until the customer
  // connects, then auto-collapses. A small "Hilfe anzeigen"-Button stays.
  // User can also dismiss manually; localStorage remembers the choice so the
  // box doesn't keep popping back open after a page refresh.
  const [helpVisible, setHelpVisible] = useState(() => {
    try {
      // v2 key: reset dismissal state for the dashboard-launch broadcast so
      // every customer sees the fresh guide by default (even if they had
      // dismissed the earlier version).
      return localStorage.getItem("mt5HelpDismissed_v2") !== "1";
    } catch {
      return true;
    }
  });
  const dismissHelp = () => {
    try { localStorage.setItem("mt5HelpDismissed_v2", "1"); } catch { /* ignore quota errors */ }
    setHelpVisible(false);
  };
  const reopenHelp = () => {
    try { localStorage.removeItem("mt5HelpDismissed_v2"); } catch { /* ignore */ }
    setHelpVisible(true);
  };

  // NOTE: We intentionally do NOT auto-dismiss the help box when the customer
  // becomes ``has_credentials=true`` because some accounts may be flagged as
  // connected without a real MyFxBook link (e.g. legacy accounts). The
  // customer must dismiss it manually once they verified the dashboard shows
  // real data. That manual dismiss persists via ``localStorage``.
  useEffect(() => {
    if (user === false) navigate("/login", { replace: true, state: { from: "/dashboard" } });
  }, [user, navigate]);

  usePageSEO({ title: "Live-Dashboard · Alphalane", noIndex: true });

  const load = async (r) => {
    if (!user?.id) return;
    try {
      const { data } = await authClient.get(`/me/dashboard?range=${r || range}`);
      setData(data);
      if (data?.error) {
        toast.error(data.error);
        // Console-Log für Support-Diagnose (User kann Screenshot machen).
        if (data.error_detail) {
          console.error("[Dashboard error_detail]", data.error_detail);
        }
      }
    } catch {
      toast.error("Konnte Dashboard nicht laden.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { if (user?.id) load(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id, range]);

  // Full sync: triggers a live MyFxBook re-pull, then reloads the dashboard.
  // Used by the "Aktualisieren" button so the customer can force-refresh
  // without waiting for the hourly cron.
  const forceSync = async () => {
    if (!user?.id) return;
    setRefreshing(true);
    try {
      if (data?.has_credentials) {
        try {
          const r = await authClient.post("/me/myfxbook/sync");
          if (r.data?.ok === false && r.data?.error) {
            toast.error(r.data.error, { duration: 12000 });
          } else if (r.data?.history_error) {
            toast.error(`Trade-Historie: ${r.data.history_error}`);
          }
        } catch (e) {
          // Sync errors are non-fatal — we still reload the dashboard from DB.
          const msg = e.response?.data?.detail || e.message;
          toast.error(`MyFxBook-Sync fehlgeschlagen: ${msg}`);
        }
      }
      await load(range);
    } finally {
      setRefreshing(false);
    }
  };

  if (user === null || loading) {
    return <div className="min-h-screen bg-[var(--al-bg)] flex items-center justify-center text-slate-500">Lade Dashboard…</div>;
  }
  if (!user || !user.id) return null;

  const current = data?.current || {};
  const kpis = data?.kpis || {};
  const chart = data?.chart || [];
  const recent = data?.recent_trades || [];
  const cashFlow = data?.cash_flow || [];
  const hasAnyData = chart.length > 0 || recent.length > 0 || current.equity != null;

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="dashboard-page">
      <Header user={user} onLogout={async () => { await logout(); navigate("/"); }} />

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <QACommunityBanner className="mb-6" storageKey="qa-banner-customer-dismissed" />
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <Link to="/onboarding" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-2" data-testid="back-to-onboarding">
              <ArrowLeft className="h-3 w-3" /> Zurück zum Onboarding
            </Link>
            <h1 className="font-display font-bold text-3xl sm:text-5xl tracking-tight">
              Dein <span className="al-grad-text">Live-Dashboard</span>
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Hallo {user.first_name} — hier siehst du deinen Kontostand und die System-Performance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={forceSync}
              disabled={refreshing}
              data-testid="refresh-dashboard"
              className="al-cta-ghost !py-2 !px-3 text-sm"
              title="Live-Sync mit MyFxBook + Dashboard neu laden"
            >
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Aktualisieren
            </button>
          </div>
        </div>

        {!data?.has_credentials && (
          <ConnectBanner
            onConnect={() => setShowConnect(true)}
            hadDataBefore={hasAnyData}
          />
        )}

        {data?.has_credentials && data?.sync_error && (
          <div
            data-testid="sync-error-banner"
            className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 flex items-start gap-3"
          >
            <span className="mt-0.5 text-amber-600 flex-shrink-0">⚠️</span>
            <div className="text-sm text-amber-900 leading-relaxed">
              <div className="font-bold mb-0.5">Synchronisierung aktuell gestört</div>
              {data.sync_error}
              {/captcha/i.test(data.sync_error || "") && (
                <div className="mt-2 rounded-lg bg-amber-100/70 border border-amber-200 px-3 py-2 text-[13px]" data-testid="captcha-support-tip">
                  <strong>Tipp:</strong> Du kannst den MyFxBook-Support bitten, das Captcha dauerhaft von deinem
                  Account zu entfernen — per E-Mail an{" "}
                  <a
                    href="mailto:support@myfxbook.com?subject=Please%20remove%20captcha%2Fsecurity%20flag&body=Hello%2C%20please%20remove%20the%20captcha%2Fsecurity%20flag%20from%20my%20account.%20Thank%20you!"
                    className="font-semibold underline"
                    data-testid="captcha-support-mail-link"
                  >
                    support@myfxbook.com
                  </a>{" "}
                  oder direkt im MyFxBook-Dashboard (Kontakt/Support). Text: <em>„Please remove the captcha/security flag from my account."</em>
                </div>
              )}
              {data?.last_sync_at && (
                <div className="mt-1 text-[12px] text-amber-700">
                  Letzte erfolgreiche Aktualisierung: {new Date(data.last_sync_at).toLocaleString("de-CH")} — deine Daten unten entsprechen diesem Stand.
                </div>
              )}
            </div>
          </div>
        )}

        {/* MT5 Investor-Password Hilfe-Box
            — visible by default until customer connects
            — once connected: hidden, replaced by a small "Hilfe anzeigen"-Pill
            — customer can dismiss manually anytime via X button */}
        {helpVisible && (
          <Mt5HelpBox onDismiss={dismissHelp} connected={!!data?.has_credentials} />
        )}
        {!helpVisible && (
          <div className="mb-6 -mt-2">
            <button
              type="button"
              onClick={reopenHelp}
              data-testid="mt5-help-reopen"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 text-xs font-semibold transition-colors"
            >
              <HelpCircle className="h-3.5 w-3.5" /> So findest du dein Investor-Passwort
            </button>
          </div>
        )}

        {/* KPI Cards — Balance (Hero), Week, Month, Gesamtprofit (Netto) */}
        <div
          className="relative overflow-hidden rounded-2xl p-6 sm:p-8 mb-4 text-white shadow-lg"
          style={{ background: "var(--al-grad)" }}
          data-testid="kpi-equity"
        >
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10" aria-hidden="true" />
          <div className="absolute -bottom-14 right-24 h-32 w-32 rounded-full bg-white/[0.07]" aria-hidden="true" />
          <div className="relative flex items-center gap-2 text-white/80">
            <Wallet className="h-5 w-5" />
            <span className="text-[11px] sm:text-xs uppercase tracking-widest font-semibold">Aktueller Kontostand</span>
          </div>
          <div className="relative mt-2 font-display font-bold text-4xl sm:text-5xl tracking-tight" data-testid="kpi-equity-value">
            {current.equity != null ? `$ ${formatNumber(current.equity)}` : "—"}
          </div>
          <div className="relative mt-2 text-xs sm:text-sm text-white/70 flex items-center gap-3 flex-wrap" data-testid="trading-status">
            <span className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${current.open_positions > 0 ? "bg-emerald-400 animate-pulse" : "bg-white/50"}`} />
              {current.open_positions > 0
                ? `Aktiv · ${current.open_positions} offene Position${current.open_positions === 1 ? "" : "en"}`
                : "Flat — keine offenen Positionen"}
            </span>
            {kpis.max_drawdown_pct != null && (
              <span>Max DD {formatNumber(kpis.max_drawdown_pct)} %</span>
            )}
            <span>{current.as_of ? `Stand ${formatDate(current.as_of)}` : "Keine Daten"}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="kpi-cards">
          <KpiCard
            testid="kpi-week"
            icon={kpis.week_pl_usd >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
            label="Diese Woche"
            value={kpis.week_pl_usd != null ? `${kpis.week_pl_usd >= 0 ? "+" : "−"}$ ${formatNumber(Math.abs(kpis.week_pl_usd))}` : "—"}
            valueClass={kpis.week_pl_usd == null ? "" : kpis.week_pl_usd >= 0 ? "text-emerald-600" : "text-rose-600"}
            footnote="Aktuelle Woche — wie MyFxBook"
          />
          <KpiCard
            testid="kpi-month"
            icon={kpis.month_pl_usd >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
            label="Diesen Monat"
            value={kpis.month_pl_usd != null ? `${kpis.month_pl_usd >= 0 ? "+" : "−"}$ ${formatNumber(Math.abs(kpis.month_pl_usd))}` : "—"}
            valueClass={kpis.month_pl_usd == null ? "" : kpis.month_pl_usd >= 0 ? "text-emerald-600" : "text-rose-600"}
            footnote="Aktueller Monat — wie MyFxBook"
          />
          <KpiCard
            testid="kpi-ytd"
            icon={<Calendar className="h-5 w-5" />}
            label={`YTD ${new Date().getFullYear()}`}
            value={kpis.ytd_pl_usd != null ? `${kpis.ytd_pl_usd >= 0 ? "+" : "−"}$ ${formatNumber(Math.abs(kpis.ytd_pl_usd))}` : "—"}
            valueClass={kpis.ytd_pl_usd == null ? "" : kpis.ytd_pl_usd >= 0 ? "text-emerald-600" : "text-rose-600"}
            footnote="Seit Jahresbeginn — netto"
          />
          <KpiCard
            testid="kpi-realized-pnl"
            icon={<CheckCircle2 className="h-5 w-5" />}
            label="Gesamtprofit"
            value={kpis.net_profit != null ? `${kpis.net_profit >= 0 ? "+" : ""}$ ${formatNumber(kpis.net_profit)}` : "—"}
            valueClass={kpis.net_profit == null ? "" : kpis.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"}
            footnote="Netto"
          />
        </div>

        {/* Equity Chart */}
        <section className="al-card p-5 sm:p-7 mb-8" data-testid="equity-chart-section">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Equity-Verlauf</div>
              <div className="font-display font-bold text-xl mt-1">{RANGE_LABELS[range]}</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {current.open_positions > 0 && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 text-xs font-display font-semibold">
                  <Activity className="h-3 w-3 animate-pulse" /> {current.open_positions} offene Position{current.open_positions === 1 ? "" : "en"}
                </div>
              )}
              <div className="inline-flex rounded-full bg-slate-100 p-1 border border-slate-200" data-testid="equity-range-toggle">
                {RANGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRange(opt.value)}
                    data-testid={`range-${opt.value}`}
                    className={`px-3 py-1 text-xs font-display font-semibold rounded-full transition-colors ${
                      range === opt.value
                        ? "bg-white text-violet-700 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {opt.short}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {chart.length === 0 ? (
            <EmptyState
              icon={<Activity className="h-8 w-8" />}
              title="Noch keine Performance-Daten"
              subtitle="Sobald dein MT5-Konto verbunden ist (oder dein Trader die ersten Snapshots eingetragen hat), erscheinen sie hier."
            />
          ) : (
            <div className="h-72" data-testid="equity-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickFormatter={(d) => {
                      // 7d/30d/90d → MM-DD, 1y/all → MMM YY
                      if (range === "1y" || range === "all") {
                        const [y, m] = d.split("-");
                        return `${["", "Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"][parseInt(m, 10)]} ${y.slice(2)}`;
                      }
                      return d.slice(5);
                    }}
                    minTickGap={range === "all" ? 60 : range === "1y" ? 40 : 24}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, fontSize: 12 }}
                    formatter={(v) => [`$ ${formatNumber(v)}`, "Equity"]}
                    labelFormatter={formatDate}
                  />
                  {chart[0]?.equity != null && <ReferenceLine y={chart[0].equity} stroke="#94a3b8" strokeDasharray="3 3" />}
                  <Line type="monotone" dataKey="equity" stroke="#8B5CF6" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Monatsprofite in $ — immer sichtbar */}
        <MonthlyPlBars series={data?.monthly_pl_series || []} />

        {/* Tab-Navigation für die Detail-Bereiche */}
        <div className="flex items-center gap-1.5 mb-5 overflow-x-auto pb-1" data-testid="dashboard-tabs">
          {[
            { id: "performance", label: "Performance", icon: LineChartIcon },
            { id: "risiko", label: "Risiko", icon: ShieldCheck },
            { id: "portfolio", label: "Portfolio", icon: Briefcase },
            { id: "historie", label: "Historie", icon: History },
            { id: "dokumente", label: "Dokumente", icon: FileText },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              data-testid={`dashboard-tab-${id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-display font-semibold whitespace-nowrap transition-colors border ${
                tab === id
                  ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "performance" && (
          <div data-testid="tab-performance">
            <YearOverview series={data?.monthly_pl_series || []} totalGainPct={kpis.total_gain_pct} />
            <RecentTradesTable recent={recent} totalTrades={kpis.total_trades} />
          </div>
        )}
        {tab === "risiko" && <RiskMetrics kpis={kpis} />}
        {tab === "portfolio" && (
          <PortfolioTab
            openTrades={data?.open_trades || []}
            openCount={current.open_positions || 0}
            unrealized={kpis.unrealized_pnl}
            symbols={data?.symbol_distribution || []}
          />
        )}
        {tab === "historie" && (
          <div data-testid="tab-historie">
            <CashFlowHistory cashFlow={cashFlow} />
            <ReportDownloads />
          </div>
        )}
        {tab === "dokumente" && <DocumentsTab />}

        {/* MyFxBook Connection card */}
        <section className="al-card p-5 sm:p-7" data-testid="mt5-connection-section">
          <div className="flex flex-wrap items-start gap-4">
            <div className={`h-12 w-12 rounded-2xl text-white flex items-center justify-center flex-shrink-0 ${data?.has_credentials ? "bg-emerald-500" : "al-grad-bg"}`}>
              {data?.has_credentials ? <CheckCircle2 className="h-5 w-5" /> : <Plug className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-bold text-lg">MT5-Konto via MyFxBook verbinden</h2>
                {data?.has_credentials && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-testid="myfxbook-status-badge">
                    <CheckCircle2 className="h-3 w-3" /> Verbunden
                  </span>
                )}
              </div>
              {data?.has_credentials ? (
                <MyFxBookStatusStrip user={user} onSyncedRefresh={load} />
              ) : (
                <p className="mt-1 text-sm text-slate-600">
                  Verbinde dein MT5-Konto über MyFxBook — dein Equity-Chart, Trades und deine Performance werden dann stündlich aktualisiert.
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => setShowConnect(true)}
                  data-testid="connect-mt5-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--al-purple)] text-[var(--al-purple)] hover:bg-[var(--al-purple)] hover:text-white text-sm font-display font-semibold transition-all"
                >
                  <Plug className="h-4 w-4" /> {data?.has_credentials ? "Verbindung aktualisieren" : "MyFxBook verbinden"}
                </button>
                {data?.has_credentials && (
                  <>
                    <button
                      onClick={async () => {
                        const t = toast.loading("Synchronisiere mit MyFxBook…");
                        try {
                          const r = await authClient.post("/me/myfxbook/sync");
                          if (r.data?.ok) {
                            toast.success("Sync erfolgreich — Daten aktualisiert.", { id: t });
                            load();
                          } else {
                            toast.error(`Sync fehlgeschlagen: ${r.data?.error || "unbekannter Fehler"}`, { id: t });
                          }
                        } catch (err) {
                          toast.error(`Sync fehlgeschlagen: ${err.response?.data?.detail || err.message}`, { id: t });
                        }
                      }}
                      data-testid="myfxbook-sync-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-display font-semibold transition-all"
                    >
                      <RefreshCw className="h-4 w-4" /> Jetzt synchronisieren
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm("MyFxBook-Verbindung wirklich trennen? Deine historischen Daten bleiben erhalten, aber neuer Sync stoppt.")) return;
                        try {
                          await authClient.delete("/me/myfxbook");
                          toast.success("MyFxBook getrennt.");
                          load();
                        } catch (err) {
                          toast.error(`Trennen fehlgeschlagen: ${err.response?.data?.detail || err.message}`);
                        }
                      }}
                      data-testid="myfxbook-disconnect-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-display font-semibold transition-all"
                    >
                      Trennen
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {showConnect && (
        <ConnectMyFxBookModal
          onClose={() => setShowConnect(false)}
          onConnected={() => { setShowConnect(false); load(); }}
        />
      )}
    </div>
  );
}

// ----------------- Sub-components -----------------
const Header = ({ user, onLogout }) => (
  <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
      <Link to="/" className="flex items-center gap-3">
        <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
        <div className="font-display font-semibold text-[15px]">
          <span className="al-grad-text">ALPHALANE</span>
          <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest hidden sm:inline">Dashboard</span>
        </div>
      </Link>
      <div className="flex items-center gap-3">
        <div className="hidden sm:block text-xs text-right">
          <div className="font-semibold text-slate-900">{user.first_name} {user.last_name}</div>
          <div className="text-slate-500">{user.email}</div>
        </div>
        <button onClick={onLogout} className="al-cta-ghost !py-2 !px-3 text-sm" data-testid="dashboard-logout-btn">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  </header>
);

const KpiCard = ({ testid, icon, label, value, valueClass = "", footnote }) => (
  <div className="al-card p-4 sm:p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500">
      {icon}
      <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
    </div>
    <div className={`mt-2 font-display font-bold text-2xl sm:text-3xl tracking-tight ${valueClass}`}>{value}</div>
    {footnote && <div className="mt-1 text-xs text-slate-500">{footnote}</div>}
  </div>
);

const EmptyState = ({ icon, title, subtitle }) => (
  <div className="text-center py-12 text-slate-500">
    <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3 text-slate-400">
      {icon}
    </div>
    <div className="font-display font-semibold text-slate-700">{title}</div>
    <p className="mt-1 text-xs max-w-md mx-auto">{subtitle}</p>
  </div>
);

const ConnectBanner = ({ onConnect, hadDataBefore }) => {
  // Two variants:
  //  - "reconnect" (hadDataBefore=true): customer was previously connected
  //    but got disconnected (e.g. admin reset). Red/urgent styling.
  //  - "first-connect" (hadDataBefore=false): brand-new customer that
  //    hasn't set up MyFxBook yet. Amber/friendly styling.
  const reconnect = !!hadDataBefore;
  const border = reconnect ? "border-rose-300" : "border-amber-200";
  const bg = reconnect ? "bg-rose-50/70" : "bg-amber-50/60";
  const iconColor = reconnect ? "text-rose-600" : "text-amber-600";
  const titleColor = reconnect ? "text-rose-900" : "text-amber-900";
  const textColor = reconnect ? "text-rose-800" : "text-amber-800";
  const btnClass = reconnect
    ? "bg-rose-600 hover:bg-rose-700"
    : "bg-amber-500 hover:bg-amber-600";
  const title = reconnect
    ? "Bitte MyFxBook neu verknüpfen"
    : "Verbinde dein MT5-Konto via MyFxBook für Live-Daten";
  const message = reconnect
    ? "Deine bisherige MyFxBook-Verbindung wurde zurückgesetzt. Damit dein Dashboard wieder Live-Daten anzeigt, verknüpfe dein MyFxBook-Konto bitte erneut — dauert nur ~2 Minuten. Danach werden Equity, Trades und Performance automatisch synchronisiert."
    : "Sobald du dein MyFxBook-Konto verbunden hast, werden Equity, Trades und Performance stündlich synchronisiert und hier live angezeigt.";
  const ctaLabel = reconnect ? "Jetzt neu verknüpfen" : "Jetzt verbinden";

  return (
    <div className={`al-card p-5 sm:p-6 mb-8 border-2 ${border} ${bg}`} data-testid="connect-banner">
      <div className="flex items-start gap-3 flex-wrap">
        <AlertCircle className={`h-5 w-5 ${iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className={`font-display font-bold ${titleColor}`}>{title}</div>
          <p className={`mt-1 text-sm ${textColor}`}>{message}</p>
          <button
            onClick={onConnect}
            data-testid="connect-banner-cta"
            className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full ${btnClass} text-white text-sm font-display font-semibold transition-all`}
          >
            <Plug className="h-4 w-4" /> {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConnectMyFxBookModal = ({ onClose, onConnected }) => {
  const [step, setStep] = useState("login"); // "login" | "pick"
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [debugResult, setDebugResult] = useState(null);

  const runDebug = async () => {
    if (!form.email || !form.password) {
      toast.error("Bitte zuerst E-Mail + Passwort eingeben.");
      return;
    }
    setBusy(true);
    setDebugResult(null);
    try {
      const res = await authClient.post("/me/myfxbook/debug-connect", form);
      setDebugResult(res.data);
    } catch (err) {
      setDebugResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const copyDebug = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugResult, null, 2));
      toast.success("Debug-Info kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authClient.post("/me/myfxbook/connect", form);
      const list = res.data?.accounts || [];
      if (list.length === 0) {
        toast.error("Keine MyFxBook-Konten gefunden. Bitte in MyFxBook zuerst ein MT5-Konto hinzufügen.");
        setBusy(false);
        return;
      }
      setAccounts(list);
      // If only one account exists, skip the picker and select it immediately.
      if (list.length === 1) {
        await pickAccount(list[0].id);
        return;
      }
      setStep("pick");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Login fehlgeschlagen: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const pickAccount = async (accountId) => {
    setBusy(true);
    try {
      const res = await authClient.post("/me/myfxbook/select-account", { account_id: accountId });
      if (res.data?.sync?.ok) {
        toast.success("MyFxBook verbunden — erste Daten geladen.");
      } else {
        toast.success("MyFxBook verbunden. Initialer Sync läuft im Hintergrund.");
      }
      onConnected();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Account-Auswahl fehlgeschlagen: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="mt5-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        {step === "login" && (
          <>
            <h2 className="font-display font-bold text-2xl mb-2">MyFxBook verbinden</h2>
            <p className="text-sm text-slate-600 mb-3">
              Gib deine <strong>MyFxBook</strong>-Zugangsdaten ein (die E-Mail + das Passwort, mit denen du dich auf <span className="font-mono-tab">myfxbook.com</span> registriert hast).
            </p>
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-900 leading-relaxed">
                <strong>Wichtig:</strong> Nicht dein MT5-Login/Passwort — deine <strong>MyFxBook</strong>-Zugangsdaten.
                Falls du dich frisch bei MyFxBook registriert hast: bitte zuerst die Bestätigungs-Email anklicken UND dein MT5-Konto in MyFxBook via <em>{`„Add Account → Auto Update via MT5"`}</em> hinzufügen.
              </p>
            </div>
            <form onSubmit={submitLogin} className="space-y-3">
              <Field label="MyFxBook E-Mail" required>
                <input type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="al-input-sm" data-testid="mt5-login-input" />
              </Field>
              <Field label="MyFxBook Passwort" required>
                <input type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="al-input-sm" data-testid="mt5-password-input" />
              </Field>
              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={onClose} className="al-cta-ghost !py-2 !px-3 text-sm" data-testid="mt5-cancel">
                  Abbrechen
                </button>
                <button type="submit" disabled={busy} data-testid="mt5-submit"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full al-grad-bg text-white text-sm font-display font-semibold disabled:opacity-50 transition-all">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                  Verbinden
                </button>
              </div>
            </form>

            {/* Debug-Test: hilft uns die genaue MyFxBook-Antwort zu sehen */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={runDebug}
                disabled={busy}
                data-testid="myfxbook-debug-btn"
                className="text-xs text-slate-500 hover:text-slate-700 font-mono-tab flex items-center gap-1"
              >
                🔧 Debug-Test durchführen (zeigt genaue MyFxBook-Antwort)
              </button>
              {debugResult && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="myfxbook-debug-result">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Debug-Ergebnis</div>
                    <button type="button" onClick={copyDebug} className="text-[10px] text-violet-700 font-semibold hover:underline">
                      📋 Kopieren
                    </button>
                  </div>
                  <pre className="text-[10px] font-mono-tab text-slate-700 whitespace-pre-wrap break-all max-h-64 overflow-auto">
                    {JSON.stringify(debugResult, null, 2)}
                  </pre>
                  <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
                    <strong>{`Wenn "ip_stable": false`}</strong> → unser Server hat wechselnde IPs. MyFxBook lehnt dann Sessions ab.
                    <br /><strong>{`Wenn "ip_stable": true aber „Invalid session"`}</strong> → anderer Grund (Cloudflare, Konto nicht verifiziert, o.ä.). Bitte diesen JSON-Text an mich schicken.
                  </div>
                </div>
              )}
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Noch kein MyFxBook-Account? <a href="https://www.myfxbook.com/members/signup" target="_blank" rel="noopener noreferrer" className="text-violet-700 font-semibold underline">Hier kostenlos registrieren →</a>
            </p>
          </>
        )}
        {step === "pick" && (
          <>
            <h2 className="font-display font-bold text-2xl mb-2">MT5-Konto wählen</h2>
            <p className="text-sm text-slate-600 mb-5">
              Wähle, welches deiner MyFxBook-Konten mit deinem Alphalane-Dashboard gespiegelt werden soll.
            </p>
            <div className="space-y-2 max-h-80 overflow-auto" data-testid="myfxbook-account-list">
              {accounts.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => pickAccount(a.id)}
                  disabled={busy}
                  data-testid={`myfxbook-account-${a.id}`}
                  className="w-full text-left border border-slate-200 rounded-xl p-3 hover:border-violet-400 hover:bg-violet-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-sm text-slate-900 truncate">{a.name || `Account #${a.accountId}`}</div>
                      <div className="text-xs text-slate-500 truncate">MT5 #{a.accountId} · {a.server || "—"}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-sm font-mono-tab font-bold ${(a.gain || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {(a.gain || 0) >= 0 ? "+" : ""}{Number(a.gain || 0).toFixed(1)}%
                      </div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{a.currency || "USD"}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={() => setStep("login")} className="al-cta-ghost !py-2 !px-3 text-sm">
                ← Zurück
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}{required && <span className="text-rose-500"> *</span>}</span>
    <div className="mt-1">{children}</div>
  </label>
);

/**
 * MyFxBookStatusStrip — small info block below the section title that
 * displays: connected MyFxBook email, MT5 account nr, last sync timestamp
 * ("vor X Minuten") and any surfaced error so the customer immediately sees
 * whether the mirror is healthy.
 */
const MyFxBookStatusStrip = ({ user }) => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const r = await authClient.get("/me/myfxbook/status");
        if (!abort) setStatus(r.data);
      } catch {
        // silently ignore — the strip is purely informational
      }
    })();
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const relative = (iso) => {
    if (!iso) return "noch nie";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "gerade eben";
    if (mins < 60) return `vor ${mins} Min.`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    const days = Math.floor(hours / 24);
    return `vor ${days} Tag${days === 1 ? "" : "en"}`;
  };

  return (
    <div className="mt-2 text-sm text-slate-600 space-y-0.5" data-testid="myfxbook-status-strip">
      <div>
        MyFxBook: <strong className="text-slate-900">{status?.email || "—"}</strong>
        {status?.account_name && (
          <span className="text-slate-500"> · Account: <strong className="font-mono-tab text-slate-800">{status.account_name}</strong></span>
        )}
      </div>
      <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
        <span>MT5-Konto: <strong className="font-mono-tab text-slate-800">{user.mt5_account || "—"}</strong></span>
        <span>·</span>
        <span>Zuletzt synchronisiert: <strong className="text-slate-700">{relative(status?.last_sync_at)}</strong></span>
        <span>·</span>
        <span>Sync stündlich automatisch</span>
      </div>
      {status?.last_error && (
        <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 flex items-start gap-2" data-testid="myfxbook-status-error">
          <AlertCircle className="h-4 w-4 text-rose-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-rose-800">
            <strong>Letzter Sync-Fehler:</strong> {status.last_error}
          </div>
        </div>
      )}
    </div>
  );
};

const formatNumber = (n) => {
  if (n == null) return "—";
  return Number(n).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-CH", { day: "2-digit", month: "short" });
};
const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-CH", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

/**
 * ReportDownloads — combined section that lets customers download both the
 * monthly PDF report and the yearly PDF report. The monthly variant is also
 * dispatched automatically on the 1st of every month by ``scheduler.py``.
 */
const ReportDownloads = () => {
  const now = new Date();
  // Default to the previous month (which is what customers usually want).
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [mYear, setMYear] = useState(lastMonth.getFullYear());
  const [mMonth, setMMonth] = useState(lastMonth.getMonth() + 1);
  const [yYear, setYYear] = useState(now.getFullYear() - 1);
  const [busy, setBusy] = useState(null); // "monthly" | "yearly" | null

  const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
  const years = Array.from({ length: 3 }, (_, i) => now.getFullYear() - i);
  const yearYears = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const url = kind === "yearly"
        ? `/me/reports/yearly?year=${yYear}`
        : `/me/reports/monthly?year=${mYear}&month=${mMonth}`;
      const res = await authClient.get(url, { responseType: "blob" });
      // When the server returns a JSON error but responseType is "blob", the
      // browser wraps the error body in a Blob. Detect that and unwrap.
      if (res.data && res.data.type && res.data.type.includes("json")) {
        const text = await res.data.text();
        try {
          const j = JSON.parse(text);
          throw new Error(j.detail || text);
        } catch {
          throw new Error(text);
        }
      }
      const blob = new Blob([res.data], { type: "application/pdf" });
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = kind === "yearly"
        ? `Alphalane-Jahresreport-${yYear}.pdf`
        : `Alphalane-Monatsreport-${mYear}-${String(mMonth).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success("Report heruntergeladen.");
    } catch (err) {
      // axios wraps HTTP errors — for blob responseType, err.response.data is
      // itself a Blob. Try to parse it as JSON to extract our custom detail.
      let detail = err.message;
      const respData = err.response?.data;
      if (respData instanceof Blob) {
        try {
          const text = await respData.text();
          const j = JSON.parse(text);
          detail = j.detail || text || err.message;
        } catch {
          /* fall back to err.message */
        }
      } else if (respData?.detail) {
        detail = respData.detail;
      }
      toast.error(`Download fehlgeschlagen: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="al-card p-5 sm:p-7 mb-8" data-testid="reports-section">
      <div className="flex items-baseline gap-3 mb-5 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Reports</div>
          <div className="font-display font-bold text-xl mt-1">Monats- & Jahres-Reports als PDF</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Monthly report */}
        <div className="rounded-2xl border border-[var(--al-line)] bg-white p-5" data-testid="monthly-report-block">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
              <Download className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base">Monats-Report</h3>
              <p className="mt-1 text-xs text-slate-600">
                Alle Trades, Ein-/Auszahlungen und Return für einen Monat. Kommt am 1. jedes Monats auch per E-Mail.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={mMonth}
              onChange={(e) => setMMonth(parseInt(e.target.value, 10))}
              data-testid="monthly-report-month"
              className="al-input-sm !py-2 !px-3 text-sm"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              value={mYear}
              onChange={(e) => setMYear(parseInt(e.target.value, 10))}
              data-testid="monthly-report-year"
              className="al-input-sm !py-2 !px-3 text-sm"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={() => download("monthly")}
              disabled={busy !== null}
              data-testid="monthly-report-download"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full al-grad-bg text-white text-sm font-display font-semibold disabled:opacity-50 transition-all"
            >
              {busy === "monthly" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Herunterladen
            </button>
          </div>
        </div>

        {/* Yearly report */}
        <div className="rounded-2xl border border-[var(--al-line)] bg-white p-5" data-testid="yearly-report-block">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <Calendar className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base">Jahres-Report</h3>
              <p className="mt-1 text-xs text-slate-600">
                Kompletter Jahresüberblick inkl. aller Monats-Returns, Trade-Statistik und Kapitalfluss.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={yYear}
              onChange={(e) => setYYear(parseInt(e.target.value, 10))}
              data-testid="yearly-report-year"
              className="al-input-sm !py-2 !px-3 text-sm"
            >
              {yearYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={() => download("yearly")}
              disabled={busy !== null}
              data-testid="yearly-report-download"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full al-grad-bg text-white text-sm font-display font-semibold disabled:opacity-50 transition-all"
            >
              {busy === "yearly" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Herunterladen
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * CashFlowHistory — chronological list of Deposits + Withdrawals pulled from
 * MyFxBook's ``get-history`` (non-trading entries). Collapsed by default:
 * only the title is visible until the customer expands the section.
 */
const CashFlowHistory = ({ cashFlow }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Sort: aggregated-delta rows first (so partial/synthesised sums are
  // always visible even when the customer has many dated entries),
  // then dated entries newest-first.
  const sorted = [...(cashFlow || [])].sort((a, b) => {
    const aAgg = a.action_raw === "aggregated_delta" || a.action_raw === "aggregated";
    const bAgg = b.action_raw === "aggregated_delta" || b.action_raw === "aggregated";
    if (aAgg && !bAgg) return -1;
    if (!aAgg && bAgg) return 1;
    return (b.date || "").localeCompare(a.date || "");
  });
  const visible = expanded ? sorted : sorted.slice(0, 5);

  const hasList = sorted.length > 0;
  return (
    <section className="al-card p-5 sm:p-7 mb-8" data-testid="cash-flow-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="cash-flow-section-toggle"
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Kapitalfluss</div>
          <div className="font-display font-bold text-xl mt-1">Einzahlungen & Auszahlungen</div>
        </div>
        <ChevronDown className={`h-5 w-5 text-slate-500 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (!hasList ? (
        <EmptyState
          icon={<ArrowDownCircle className="h-8 w-8" />}
          title="Noch keine Transaktionen"
          subtitle="Sobald du Einzahlungen oder Auszahlungen vornimmst, erscheinen sie hier automatisch."
        />
      ) : (
        <>
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm" data-testid="cash-flow-table">
              <thead className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 border-b border-[var(--al-line)]">
                <tr>
                  <th className="text-left py-2">Datum</th>
                  <th className="text-left py-2">Art</th>
                  <th className="text-right py-2">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m, i) => {
                  const isAgg = m.action_raw === "aggregated_delta" || m.action_raw === "aggregated";
                  const isFee = m.kind === "withdrawal" && m.is_fee;
                  let badgeClass, badgeIcon, badgeLabel;
                  if (m.kind === "deposit") {
                    badgeClass = "bg-emerald-100 text-emerald-700";
                    badgeIcon = <ArrowDownCircle className="h-3 w-3" />;
                    badgeLabel = "Einzahlung";
                  } else if (isFee) {
                    badgeClass = "bg-violet-100 text-violet-700";
                    badgeIcon = <Percent className="h-3 w-3" />;
                    badgeLabel = "Performance-Fee";
                  } else {
                    badgeClass = "bg-rose-100 text-rose-700";
                    badgeIcon = <ArrowUpCircle className="h-3 w-3" />;
                    badgeLabel = "Auszahlung";
                  }
                  const amountClass = m.kind === "deposit"
                    ? "text-emerald-600"
                    : isFee ? "text-violet-600" : "text-rose-600";
                  return (
                    <tr key={`${m.date || "agg"}|${m.kind}|${m.amount}|${i}`} className={`border-b border-[var(--al-line)] last:border-b-0 ${isAgg ? "bg-slate-50/60" : ""}`}>
                      <td className="py-2.5 font-mono-tab text-slate-600 text-xs whitespace-nowrap">
                        {isAgg
                          ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" title="Summe aus MyFxBook, ohne Einzeltransaktionen">Aggregiert</span>
                          : formatDateTime(m.date)}
                      </td>
                      <td className="py-2.5">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${badgeClass}`}>
                          {badgeIcon}
                          {badgeLabel}
                        </span>
                      </td>
                      <td className={`py-2.5 text-right font-mono-tab font-bold ${amountClass}`}>
                        {m.kind === "deposit" ? "+" : "−"}$ {formatNumber(Math.abs(m.amount || 0))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {sorted.length > 5 && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                data-testid="cash-flow-toggle"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-display font-semibold transition-colors"
              >
                {expanded ? "Weniger anzeigen" : `Alle ${sorted.length} Transaktionen anzeigen`}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
            </div>
          )}
        </>
      ))}
    </section>
  );
};

/**
 * RecentTradesTable — renders the customer's trade history.
 * Shows the newest 10 by default and offers an "Alle anzeigen" toggle that
 * expands to the full list (backend returns up to 50). Every row shows a
 * timestamp column so the customer can see WHEN each trade happened.
 */
const RecentTradesTable = ({ recent, totalTrades }) => {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(recent.length / PAGE_SIZE));
  const visible = expanded
    ? recent.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
    : recent.slice(0, 10);
  const rangeStart = expanded ? page * PAGE_SIZE + 1 : 1;
  const rangeEnd = expanded ? Math.min((page + 1) * PAGE_SIZE, recent.length) : Math.min(10, recent.length);
  return (
    <section className="al-card p-5 sm:p-7 mb-8" data-testid="recent-trades-section">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Letzte Trades</div>
          <div className="font-display font-bold text-xl mt-1 flex items-center gap-2 flex-wrap">
            <span>Recent Activity</span>
            {totalTrades != null && (
              <span
                data-testid="total-trades-badge"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold uppercase tracking-widest"
              >
                {totalTrades} Trades gesamt
              </span>
            )}
          </div>
        </div>
        {recent.length > 10 && (
          <div className="text-xs text-slate-500">
            Zeige <strong className="text-slate-700">{rangeStart}–{rangeEnd}</strong> von <strong className="text-slate-700">{recent.length}</strong>
          </div>
        )}
      </div>

      {recent.length === 0 ? (
        <EmptyState
          icon={<Calendar className="h-8 w-8" />}
          title="Noch keine Trades"
          subtitle="Sobald das Copy-Trading aktiv ist, erscheinen alle Trades hier in Echtzeit."
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-trades-table">
              <thead className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 border-b border-[var(--al-line)]">
                <tr>
                  <th className="text-left py-2">Datum / Zeit</th>
                  <th className="text-left py-2">Symbol</th>
                  <th className="text-left py-2">Side</th>
                  <th className="text-right py-2">Open</th>
                  <th className="text-right py-2">Close</th>
                  <th className="text-right py-2">P&L</th>
                  <th className="text-right py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((t, i) => (
                  <tr key={t.id || `${t.open_time}|${t.symbol}|${i}`} className="border-b border-[var(--al-line)] last:border-b-0">
                    <td className="py-2.5 font-mono-tab text-slate-600 whitespace-nowrap text-xs">
                      <div>{formatDateTime(t.open_time)}</div>
                      {t.close_time && (
                        <div className="text-[10px] text-slate-400">→ {formatDateTime(t.close_time)}</div>
                      )}
                    </td>
                    <td className="py-2.5 font-display font-semibold">{t.symbol}</td>
                    <td className="py-2.5">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${t.side === "buy" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {t.side}
                      </span>
                    </td>
                    <td className="py-2.5 text-right font-mono-tab text-slate-700">{t.open_price}</td>
                    <td className="py-2.5 text-right font-mono-tab text-slate-700">{t.close_price ?? "—"}</td>
                    <td className={`py-2.5 text-right font-mono-tab font-bold ${t.profit == null ? "text-slate-400" : t.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {t.profit != null ? `${t.profit >= 0 ? "+" : "−"}$ ${formatNumber(Math.abs(t.profit))}` : "—"}
                    </td>
                    <td className="py-2.5 text-right">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${t.status === "open" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}`}>
                        {t.status === "open" ? "Offen" : "Geschlossen"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {recent.length > 10 && (
            <div className="mt-4 flex flex-col items-center gap-3">
              {expanded && pageCount > 1 && (
                <div className="flex items-center gap-3" data-testid="recent-trades-pagination">
                  <button
                    type="button"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    data-testid="recent-trades-prev"
                    className="px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-display font-semibold disabled:opacity-40 transition-colors"
                  >
                    ← Zurück
                  </button>
                  <span className="text-xs text-slate-500 font-display font-semibold" data-testid="recent-trades-page-info">
                    Seite {page + 1} / {pageCount}
                  </span>
                  <button
                    type="button"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    data-testid="recent-trades-next"
                    className="px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-display font-semibold disabled:opacity-40 transition-colors"
                  >
                    Weiter →
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => { setExpanded((v) => !v); setPage(0); }}
                data-testid="recent-trades-toggle"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs font-display font-semibold transition-colors"
              >
                {expanded ? "Weniger anzeigen" : `Alle ${recent.length} Trades anzeigen`}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

/**
 * Mt5HelpBox — explains how the customer connects their MT5 account to
 * MyFxBook, so Alphalane can auto-sync equity/trades hourly. Shown by
 * default until the customer connects, then collapsible.
 */
const Mt5HelpBox = ({ onDismiss, connected }) => {
  // Guide expanded by default for ALL customers (some may show as connected
  // in the DB but haven't actually completed the MyFxBook flow yet).
  // Customer can collapse via the summary toggle once done.
  const [expandedAt, setExpandedAt] = useState(true);

  return (
    <div
      className="al-card border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 mb-6 overflow-hidden"
      data-testid="mt5-help-box"
    >
      <div className="p-5 sm:p-6 flex items-start gap-4">
        <div className="hidden sm:flex h-10 w-10 rounded-xl bg-violet-600 text-white items-center justify-center flex-shrink-0">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-display font-bold text-base sm:text-lg text-slate-900 flex items-center gap-2">
              <HelpCircle className="h-4 w-4 sm:hidden text-violet-600" />
              So verbindest du dein MT5-Konto via MyFxBook mit Alphalane
            </h3>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="mt5-help-dismiss"
              aria-label="Hilfe-Box schließen"
              className="flex-shrink-0 p-1 rounded-md hover:bg-white/60 text-slate-500 transition-colors"
              title="Hilfe ausblenden — du kannst sie jederzeit wieder einblenden"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>

          {connected ? (
            <p className="text-xs sm:text-sm text-emerald-700 font-semibold mb-3 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> Dein MT5-Konto ist bereits verbunden — diese Anleitung dient nur als Referenz.
            </p>
          ) : (
            <p className="text-xs sm:text-sm text-slate-600 mb-3 leading-relaxed">
              Damit dein Live-Dashboard die Echtzeit-Zahlen deines MT5-Kontos anzeigen kann, verbindest du dein MT5-Konto über
              <strong className="text-slate-900"> MyFxBook</strong> — eine kostenlose Analyse-Plattform.
              Alphalane synchronisiert dann stündlich mit deinem MyFxBook.
            </p>
          )}

          <div className="rounded-xl bg-white/70 backdrop-blur border border-violet-100 p-3 sm:p-4 mb-3" data-testid="mt5-help-why">
            <div className="font-display font-semibold text-sm text-slate-900 mb-1.5 flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-emerald-600" /> Warum MyFxBook?
            </div>
            <p className="text-[12px] sm:text-xs text-slate-700 leading-relaxed">
              MyFxBook ist eine seit über 15 Jahren etablierte, kostenlose Plattform zur Performance-Analyse von MT5-Konten.
              Du verbindest MT5 dort mit dem <strong>Investor-Passwort (Read-Only)</strong> —
              MyFxBook (und damit auch Alphalane) kann deine Zahlen <strong>sehen</strong>, aber
              <strong> niemals handeln oder Geld bewegen</strong>. Dein Kapital bleibt zu 100% unter deiner Kontrolle.
            </p>
          </div>

          <details
            className="rounded-xl bg-white/70 backdrop-blur border border-violet-100 mb-3 group"
            open={expandedAt}
            onToggle={(e) => setExpandedAt(e.target.open)}
            data-testid="mt5-help-steps-wrap"
          >
            <summary className="cursor-pointer list-none flex items-center justify-between gap-2 p-3 sm:p-4 select-none">
              <div className="font-display font-semibold text-sm text-slate-900">
                Schritt-für-Schritt-Anleitung (dauert ~5 Minuten)
              </div>
              <span className="text-violet-600 group-open:rotate-180 transition-transform">
                <ChevronDown className="h-4 w-4" />
              </span>
            </summary>
            <ol className="px-3 sm:px-4 pb-4 space-y-2.5 text-[12px] sm:text-xs text-slate-700 leading-relaxed" data-testid="mt5-help-steps">
              <li className="flex gap-2">
                <span className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white font-bold text-[10px]">1</span>
                <span>
                  Registriere dich kostenlos bei <a href="https://www.myfxbook.com/members/signup" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline hover:text-violet-900">myfxbook.com</a> mit deiner E-Mail-Adresse.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white font-bold text-[10px]">2</span>
                <span>
                  Klicke in MyFxBook auf <strong>{`„Add Account"`}</strong> → <strong>{`„Auto Update via MT5"`}</strong>.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white font-bold text-[10px]">3</span>
                <span>
                  Gib deine <strong>RoboForex MT5-Kontonummer</strong>, das <strong>Investor-Passwort (Read-Only!)</strong> und den Server (z.B. <span className="font-mono-tab">RoboForex-ECN</span>) ein.
                  <br />
                  <span className="text-slate-500">{`Dein Investor-Passwort findest du im RoboForex-Kundenbereich unter „Meine Konten" → Passwortverwaltung.`}</span>
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white font-bold text-[10px]">4</span>
                <span>
                  {`Warte 1–2 Minuten bis MyFxBook dein Konto zum ersten Mal ausgelesen hat (grüner „Connected"-Status).`}
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white font-bold text-[10px]">5</span>
                <span>
                  Klicke hier im Alphalane-Dashboard auf <strong>{`„MyFxBook verbinden"`}</strong> und trage <strong>deine MyFxBook-E-Mail + Passwort</strong> ein.
                  Wir spiegeln dann deine Daten stündlich hierher — inkl. Equity-Chart, Trades und Statistiken.
                </span>
              </li>
            </ol>
          </details>

          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 mb-3 flex items-start gap-2" data-testid="mt5-help-warning">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] sm:text-xs text-amber-900 leading-relaxed">
              <strong>Wichtig:</strong> Verwende in MyFxBook <strong>immer</strong> das
              <strong> Investor-Passwort</strong> (Read-Only), nie das Master-Passwort.
              So kann niemand — auch nicht wir — auf deinem Konto handeln.
            </p>
          </div>

          {/* Two video slots — registration + investor-password */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div
              className="rounded-xl overflow-hidden border border-violet-200 bg-slate-900 shadow-md"
              data-testid="mt5-help-video-registration"
            >
              <div className="px-3 py-2 bg-violet-600 text-white flex items-center gap-2 text-[11px] font-display font-semibold">
                <PlayCircle className="h-4 w-4 flex-shrink-0" />
                Video 1: MyFxBook-Registrierung
              </div>
              <div className="relative" style={{ paddingTop: "56.25%" }}>
                <iframe
                  src="https://www.youtube-nocookie.com/embed/--wNLFu0ABw?rel=0&modestbranding=1"
                  title="So richtest du dein Alphalane Dashboard via MyFxBook ein"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="absolute inset-0 w-full h-full"
                  data-testid="mt5-help-video-registration-iframe"
                />
              </div>
            </div>

            <div
              className="rounded-xl overflow-hidden border border-violet-200 bg-slate-900 shadow-md"
              data-testid="mt5-help-video-investor"
            >
              <div className="px-3 py-2 bg-violet-600 text-white flex items-center gap-2 text-[11px] font-display font-semibold">
                <PlayCircle className="h-4 w-4 flex-shrink-0" />
                Video 2: Investor-Passwort finden
              </div>
              <div className="relative" style={{ paddingTop: "56.25%" }}>
                <iframe
                  src="https://www.youtube-nocookie.com/embed/GOK5A7bYxVs?rel=0&modestbranding=1"
                  title="So findest du dein RoboForex MT5-Investor-Passwort"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="absolute inset-0 w-full h-full"
                  data-testid="mt5-help-video-investor-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/** Monatsprofite in $ — Balkenchart, letzte 12 Monate (netto nach Fees). */
const MONTH_SHORT_DE = ["", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const MonthlyPlBars = ({ series }) => {
  const data = (series || []).slice(-12).map((r) => ({
    ...r,
    label: `${MONTH_SHORT_DE[parseInt(r.month.slice(5, 7), 10)]} ${r.month.slice(2, 4)}`,
  }));
  if (data.length === 0) return null;
  return (
    <section className="al-card p-5 sm:p-7 mb-8" data-testid="monthly-pl-section">
      <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Monatsprofite</div>
      <div className="font-display font-bold text-xl mt-1 mb-4">Netto-Gewinn pro Monat in $</div>
      <div className="h-52" data-testid="monthly-pl-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
            <Tooltip
              contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, fontSize: 12 }}
              formatter={(v) => [`$ ${formatNumber(v)}`, "Netto-Profit"]}
            />
            <Bar dataKey="pl" radius={[6, 6, 0, 0]}>
              {data.map((r) => (
                <Cell key={r.month} fill={r.pl >= 0 ? "#10b981" : "#f43f5e"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 text-[11px] text-slate-400">Nach Abzug der Performance-Fees.</div>
    </section>
  );
};

/** Jahresübersicht — Monats-P&L (netto) gruppiert nach Jahr, mit Jahres-Total. */
const YearOverview = ({ series, totalGainPct }) => {
  const byYear = useMemo(() => {
    const map = {};
    (series || []).forEach((r) => {
      const y = r.month.slice(0, 4);
      (map[y] = map[y] || []).push(r);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [series]);
  if (byYear.length === 0) {
    return (
      <section className="al-card p-5 sm:p-7 mb-8" data-testid="year-overview-section">
        <div className="font-display font-bold text-xl">Jahresübersicht</div>
        <div className="mt-3 text-sm text-slate-500">Noch keine Monatsdaten vorhanden.</div>
      </section>
    );
  }
  return (
    <section className="al-card p-5 sm:p-7 mb-8" data-testid="year-overview-section">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Performance</div>
          <div className="font-display font-bold text-xl mt-1">Jahresübersicht</div>
        </div>
        {totalGainPct != null && (
          <span className="text-xs text-slate-500">
            Seit Start: <strong className={`font-mono-tab ${totalGainPct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{totalGainPct >= 0 ? "+" : ""}{formatNumber(totalGainPct)} %</strong>
          </span>
        )}
      </div>
      <div className="space-y-5">
        {byYear.map(([year, rows]) => {
          const total = rows.reduce((s, r) => s + (r.pl || 0), 0);
          return (
            <div key={year} data-testid={`year-overview-${year}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-display font-bold text-base">{year}</div>
                <div className={`font-mono-tab font-bold text-sm ${total >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {total >= 0 ? "+" : "−"}$ {formatNumber(Math.abs(total))}
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                {rows.map((r) => (
                  <div key={r.month} className="px-2 py-1.5 rounded-lg border border-slate-100 bg-slate-50/60 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{MONTH_SHORT_DE[parseInt(r.month.slice(5, 7), 10)]}</div>
                    <div className={`font-mono-tab text-xs font-semibold ${r.pl >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {r.pl >= 0 ? "+" : "−"}${formatNumber(Math.abs(r.pl))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/** Risiko-Kennzahlen mit Kurz-Erklärung. */
const RiskMetrics = ({ kpis }) => {
  const items = [
    { label: "Max Drawdown", value: kpis.max_drawdown_pct != null ? `${formatNumber(kpis.max_drawdown_pct)} %` : "—",
      hint: "Größter Rückgang der Equity-Kurve deines Kontos (bereinigt um Ein-/Auszahlungen), maximal das offizielle System-DD.", testid: "risk-maxdd" },
    { label: "Max Drawdown ($)", value: kpis.max_dd_usd != null ? `$ ${formatNumber(Math.abs(kpis.max_dd_usd))}` : "—",
      hint: "Größter Rückgang der Trading-Gewinnkurve in Dollar.", testid: "risk-maxdd-usd" },
    { label: "Recovery Factor", value: kpis.recovery_factor != null ? formatNumber(kpis.recovery_factor) : "—",
      hint: "Gesamter Trading-Gewinn ÷ Max Drawdown. Je höher, desto robuster.", testid: "risk-recovery" },
    { label: "Win Rate (30 T)", value: kpis.win_rate_30d != null ? `${formatNumber(kpis.win_rate_30d)} %` : "—",
      hint: "Anteil der Gewinn-Trades in den letzten 30 Tagen.", testid: "risk-winrate" },
  ];
  return (
    <section className="al-card p-5 sm:p-7 mb-8" data-testid="tab-risiko">
      <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Risiko</div>
      <div className="font-display font-bold text-xl mt-1 mb-5">Risikokennzahlen</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.label} className="p-4 rounded-xl border border-slate-100 bg-slate-50/50" data-testid={it.testid}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{it.label}</div>
            <div className="font-display font-bold text-2xl mt-1">{it.value}</div>
            <div className="mt-1.5 text-xs text-slate-500 leading-snug">{it.hint}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

/** Portfolio — offene Positionen + Symbol-Verteilung. */
const SYMBOL_COLORS = ["#8B5CF6", "#10b981", "#f59e0b", "#3b82f6", "#f43f5e", "#14b8a6", "#a855f7", "#64748b"];
const PortfolioTab = ({ openTrades, openCount, unrealized, symbols }) => {
  const totalCount = symbols.reduce((s, r) => s + (r.count || 0), 0);
  return (
    <div data-testid="tab-portfolio">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="al-card p-5" data-testid="portfolio-open-count">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Offene Positionen</div>
          <div className="font-display font-bold text-3xl mt-1">{openCount}</div>
        </div>
        <div className="al-card p-5" data-testid="portfolio-unrealized">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Unrealisiertes P&L</div>
          <div className={`font-display font-bold text-3xl mt-1 ${(unrealized || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {unrealized != null ? `${unrealized >= 0 ? "+" : "−"}$ ${formatNumber(Math.abs(unrealized))}` : "—"}
          </div>
        </div>
      </div>

      <section className="al-card p-5 sm:p-7 mb-4" data-testid="portfolio-symbols">
        <div className="font-display font-bold text-xl mb-4">Verteilung nach Symbol</div>
        {symbols.length === 0 ? (
          <div className="text-sm text-slate-500">Noch keine Trade-Daten vorhanden.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6 items-center">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={symbols} dataKey="count" nameKey="symbol" innerRadius={55} outerRadius={85} paddingAngle={2}>
                    {symbols.map((r, i) => (
                      <Cell key={r.symbol} fill={SYMBOL_COLORS[i % SYMBOL_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} Trades`, n]} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {symbols.map((r, i) => (
                <div key={r.symbol} className="flex items-center gap-3 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: SYMBOL_COLORS[i % SYMBOL_COLORS.length] }} />
                  <span className="font-display font-semibold">{r.symbol}</span>
                  <span className="ml-auto font-mono-tab text-slate-500">
                    {totalCount ? Math.round((r.count / totalCount) * 100) : 0}% · {r.count} Trades
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3 text-[11px] text-slate-400">Basierend auf allen synchronisierten Trades (Forex/CFD via MT5).</div>
      </section>

      <section className="al-card p-5 sm:p-7" data-testid="portfolio-open-list">
        <div className="font-display font-bold text-xl mb-4">Offene Positionen</div>
        {(openTrades || []).length === 0 ? (
          <div className="text-sm text-slate-500">Aktuell keine offenen Positionen — das System ist flat.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Symbol</th><th className="py-2 pr-3">Richtung</th>
                  <th className="py-2 pr-3 text-right">Open</th>
                  <th className="py-2 text-right">P&L</th>
                </tr>
              </thead>
              <tbody>
                {openTrades.map((t, i) => {
                  const pl = t.unrealized_profit != null ? t.unrealized_profit : t.profit;
                  return (
                    <tr key={t.id || `${t.open_time}|${t.symbol}|${i}`} className="border-b border-slate-50">
                      <td className="py-2 pr-3 font-display font-semibold">{t.symbol || "—"}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${String(t.side).toLowerCase() === "buy" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                          {t.side || "—"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono-tab">{t.open_price ?? "—"}</td>
                      <td className={`py-2 text-right font-mono-tab font-semibold ${(pl || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {pl != null ? `${pl >= 0 ? "+" : "−"}$ ${formatNumber(Math.abs(pl))}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

/** Dokumente — Vertrag, Steuerunterlagen, FAQ. */
const FAQS = [
  { q: "Wie wird die Performance-Fee abgerechnet?", a: "Die Fee wird automatisch monatlich vom Broker-Konto abgebucht. Alle Abzüge siehst du transparent unter Historie → Kapitalfluss." },
  { q: "Kann ich jederzeit Geld auszahlen?", a: "Ja. Dein Konto läuft auf deinen Namen beim Broker — Auszahlungen beantragst du direkt dort, dein Geld bleibt immer in deiner Hand." },
  { q: "Was bedeutet 'Netto-Gewinn'?", a: "Dein Gewinn nach Abzug der Performance-Fees: Kontostand minus Einzahlungen plus deine Auszahlungen. Das ist der Betrag, der wirklich dir gehört." },
  { q: "Wie aktuell sind die Daten hier?", a: "Das Dashboard spiegelt dein MyFxBook-Konto. Mit 'Aktualisieren' oben löst du jederzeit einen frischen Sync aus." },
  { q: "Wo finde ich Unterlagen für die Steuererklärung?", a: "Der Jahresbericht (unter Historie → Reports bzw. hier bei Dokumente) enthält alle Kontobewegungen, Gewinne und Gebühren des Jahres als PDF." },
];
const DocumentsTab = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fees, setFees] = useState(null);
  const [feeBusy, setFeeBusy] = useState(null);

  useEffect(() => {
    authClient
      .get("/me/fees")
      .then((r) => setFees(r.data?.fees || []))
      .catch(() => setFees([]));
  }, []);

  const downloadFeeReceipt = async (f) => {
    setFeeBusy(f.key);
    try {
      const res = await authClient.get(`/me/fees/receipt?key=${encodeURIComponent(f.key)}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Alphalane-Fee-Beleg-${f.receipt_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Fee-Beleg heruntergeladen.");
    } catch {
      toast.error("Beleg konnte nicht erstellt werden — bitte später erneut versuchen.");
    } finally {
      setFeeBusy(null);
    }
  };

  const downloadContract = async () => {
    setBusy(true);
    try {
      const res = await authClient.get(`${API}/contract/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Alphalane-Vertrag.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Vertrag nicht gefunden — falls du noch nicht signiert hast, melde dich bei uns.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="tab-dokumente">
      <section className="al-card p-5 sm:p-7 mb-4">
        <div className="font-display font-bold text-xl mb-4">Deine Dokumente</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            onClick={downloadContract}
            disabled={busy}
            data-testid="download-contract"
            className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 transition-colors text-left disabled:opacity-60"
          >
            <div className="h-10 w-10 rounded-xl al-grad-bg text-white flex items-center justify-center flex-shrink-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            </div>
            <div>
              <div className="font-display font-semibold text-sm">Vertrag (signiert)</div>
              <div className="text-xs text-slate-500">Dein unterschriebener Dienstleistungsvertrag als PDF</div>
            </div>
            <Download className="h-4 w-4 text-slate-400 ml-auto flex-shrink-0" />
          </button>
          <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200">
            <div className="h-10 w-10 rounded-xl bg-slate-800 text-white flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display font-semibold text-sm">Steuerunterlagen</div>
              <div className="text-xs text-slate-500">Jahresbericht mit allen Bewegungen — unter Historie → Reports (Jahr) herunterladen</div>
            </div>
          </div>
        </div>
      </section>

      {/* Fee-Belege */}
      <section className="al-card p-5 sm:p-7 mb-4" data-testid="fee-receipts-section">
        <div className="font-display font-bold text-xl mb-1">Fee-Belege</div>
        <p className="text-xs text-slate-500 mb-4">
          Für jede automatisch abgebuchte Performance-Fee steht hier ein PDF-Beleg zum Download bereit.
        </p>
        {fees === null ? (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Lade Belege…
          </div>
        ) : fees.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="fee-receipts-empty">
            Noch keine Performance-Fee abgebucht — sobald die erste Fee eingezogen wird, erscheint der Beleg hier automatisch.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {fees.map((f) => (
              <div key={f.key} className="flex items-center gap-3 py-3" data-testid={`fee-receipt-row-${f.receipt_no}`}>
                <div className="h-9 w-9 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-sm">Performance-Fee · {formatDate(f.date)}</div>
                  <div className="text-xs text-slate-500 font-mono-tab">Beleg-Nr. {f.receipt_no}</div>
                </div>
                <div className="ml-auto flex items-center gap-3 flex-shrink-0">
                  <div className="font-mono-tab font-bold text-sm text-violet-600">$ {formatNumber(f.amount)}</div>
                  <button
                    onClick={() => downloadFeeReceipt(f)}
                    disabled={feeBusy !== null}
                    data-testid={`fee-receipt-download-${f.receipt_no}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 text-xs font-display font-semibold transition-colors disabled:opacity-50"
                  >
                    {feeBusy === f.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="al-card p-5 sm:p-7" data-testid="faq-section">
        <div className="font-display font-bold text-xl mb-4">Häufige Fragen</div>
        <div className="divide-y divide-slate-100">
          {FAQS.map((f, i) => (
            <div key={f.q}>
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between gap-3 py-3.5 text-left"
              >
                <span className="font-display font-semibold text-sm">{f.q}</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="pb-4 text-sm text-slate-600 leading-relaxed -mt-1">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
